
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageHeader from '@/components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Newspaper } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface DbArticle {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  published_at: string;
  image_url: string | null;
}

const Blog = () => {
  const [dbPosts, setDbPosts] = useState<DbArticle[]>([]);

  useEffect(() => {
    supabase
      .from('news_articles')
      .select('slug,title,excerpt,category,published_at,image_url')
      .eq('status', 'published')
      .order('published_at', { ascending: false })
      .limit(60)
      .then(({ data }) => setDbPosts((data as any) || []));
  }, []);

  const posts = [
    {
      id: 0,
      title: "Best Machine Learning Conferences 2026 – London, UK, Europe, USA, Singapore, Dubai, Saudi Arabia, Dublin, Ireland & Silicon Valley",
      slug: "best-machine-learning-conferences-2026-london-uk-europe-usa-singapore-dubai-saudi-arabia-dublin-ireland-silicon-valley/",
      excerpt: "Machine learning continues to sit at the core of artificial intelligence innovation, powering breakthroughs in enterprise automation, healthcare, finance, robotics, climate intelligence, and national AI strategies. In 2026, the global ML conference landscape reflects this momentum.",
      date: "2025-12-29",
      category: "Conference Guide"
    },
    {
      id: 1,
      title: "AI Voice Takes the Stage: Global Startups Leading the AI Revolution",
      slug: "ai-voice-takes-the-stage-global-startups-leading-the-ai-revolution/",
      excerpt: "In boardrooms and VC pitches around the world, voice is fast replacing the tap. As conversational interfaces grow more natural—and markets respond—the race is on for voice-driven Artificial Intelligence innovation.",
      date: "2025-01-30",
      category: "Article"
    },
    {
      id: 2,
      title: "Top 10 AI Startups in London UK Europe in 2024 and 2025",
      slug: "top-10-ai-startups-in-london-uk-europe-in-2024-and-2025/",
      excerpt: "Artificial Intelligence (AI) continues to revolutionize industries worldwide, and Europe, particularly London and other tech hubs across the continent, is home to some of the most innovative AI startups.",
      date: "2024-04-28",
      category: "Article"
    },
    {
      id: 3,
      title: "Top 10 Generative AI Courses Online in London, UK, and Europe",
      slug: "top-10-generative-ai-courses-online-in-london-uk-and-europe/",
      excerpt: "As generative AI continues to revolutionize industries from creative arts to healthcare, the demand for skills in this area has surged. Numerous online courses are now available, offering a range of opportunities to delve into this exciting field.",
      date: "2024-04-28",
      category: "Article"
    },
    {
      id: 4,
      title: "List of Top 10 Telecom Conferences 2025 in London, UK, Europe, Germany, Italy, France, India, China, UAE, Saudi, Turkey and USA",
      slug: "list-of-top-10-telecom-conferences-2025-in-london-uk-europe-germany-italy-france-india-china-uae-saudi-turkey-and-usa/",
      excerpt: "The telecommunications industry is continually evolving, driven by advancements in technology and changing consumer demands. Staying abreast of these developments is crucial for professionals seeking to innovate and lead in this dynamic field.",
      date: "2024-04-28",
      category: "Article"
    },
    {
      id: 5,
      title: "A Review of Latest AI Market Size Statistics by Company in 2025",
      slug: "a-review-of-latest-ai-market-size-statistics-by-company-in-2025/",
      excerpt: "The artificial intelligence (AI) industry has experienced unprecedented growth in recent years, with projections indicating a continued upward trajectory. In 2025, the global AI market size is expected to reach approximately $757.58 billion.",
      date: "2024-04-28",
      category: "Article"
    },
    {
      id: 6,
      title: "Top B2B AI Trends for 2025 in London UK, Europe, US, India, China, UAE, Saudi and Canada",
      slug: "top-b2b-ai-trends-for-2025-in-london-uk-europe-us-india-china-uae-saudi-and-canada/",
      excerpt: "In 2025, the global business landscape is being reshaped by transformative trends in B2B marketing and sales, driven by groundbreaking developments in artificial intelligence.",
      date: "2024-04-28",
      category: "Article"
    },
    {
      id: 7,
      title: "Top 10 AI Stocks List for 2025 in London UK, New York, Paris, Frankfurt, Hong Kong, India and Singapore",
      slug: "top-10-ai-stocks-list-for-2025-in-london-uk-new-york-paris-frankfurt-hong-kong-india-and-singapore/",
      excerpt: "As AI continues to transform industries globally, investors are increasingly looking to capitalize on this technology's exponential growth. This article explores promising AI stocks across major financial centers.",
      date: "2024-04-28",
      category: "Article"
    }
  ];

  const SITE = "https://aiconference.london";
  const publisher = {
    "@type": "Organization",
    "name": "AI Conference London",
    "logo": { "@type": "ImageObject", "url": `${SITE}/lovable-uploads/logo-2026.svg` }
  };

  const allPosts = [
    ...dbPosts.map(p => ({
      title: p.title,
      slug: p.slug,
      excerpt: p.excerpt,
      date: p.published_at,
      image: p.image_url || undefined,
    })),
    ...posts.map(p => ({
      title: p.title,
      slug: p.slug,
      excerpt: p.excerpt,
      date: p.date,
      image: undefined as string | undefined,
    })),
  ];

  const newsStructuredData = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "AI Conference London News",
    "description": "Latest articles and insights about AI technology and innovation",
    "url": `${SITE}/news`,
    "publisher": publisher,
    "blogPost": allPosts.map(post => ({
      "@type": "BlogPosting",
      "headline": post.title,
      "url": `${SITE}/${post.slug}`,
      "mainEntityOfPage": `${SITE}/${post.slug}`,
      "datePublished": post.date,
      "description": post.excerpt,
      ...(post.image ? { "image": post.image } : {}),
      "author": { "@type": "Organization", "name": "AI Conference London" },
      "publisher": publisher
    }))
  };

  const itemListStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "AI Conference London News Articles",
    "itemListElement": allPosts.map((post, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "url": `${SITE}/${post.slug}`,
      "name": post.title
    }))
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE },
      { "@type": "ListItem", "position": 2, "name": "News", "item": `${SITE}/news` }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A192F] via-[#172A46] to-[#1E3A5F]">
      <Helmet>
        <title>AI News - AI World Congress 2026 - November 25-26, London UK - AI Conference</title>
        <meta name="description" content="Read the latest articles and insights about AI technology and innovation." />
        <link rel="canonical" href="https://aiconference.london/news" />
        <meta property="og:title" content="AI News - AI World Congress 2026 - November 25-26, London UK - AI Conference" />
        <meta property="og:description" content="Latest articles and insights about AI technology and innovation." />
        <meta property="og:url" content="https://aiconference.london/news" />
        <meta property="og:image" content="https://aiconference.london/lovable-uploads/og-banner-2026.jpg" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="AI News - AI World Congress 2026 - November 25-26, London UK - AI Conference" />
        <meta name="twitter:description" content="Latest AI articles and insights." />
        <meta name="twitter:image" content="https://aiconference.london/lovable-uploads/og-banner-2026.jpg" />
        <script type="application/ld+json">{JSON.stringify(newsStructuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(itemListStructuredData)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbStructuredData)}</script>
      </Helmet>
      <Header />
      <PageHeader 
        title=""
        subtitle=""
        backgroundImage="/lovable-uploads/75e6360c-a56e-48e7-9e3c-5903e26ee0a6.png"
      />
      <main className="container mx-auto px-4 py-16 -mt-16 relative z-10">
        <div className="max-w-4xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-white">Latest AI News</h1>
            <Link to="/news" className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors">
              <Newspaper className="h-5 w-5" />
              <span>View News</span>
            </Link>
          </div>
          
          <div className="grid grid-cols-1 gap-6">
            {dbPosts.map((post) => (
              <Link key={post.slug} to={`/${post.slug}`}>
                <Card className="bg-gradient-to-br from-orange-500 to-black/90 hover:from-orange-600 hover:to-black transition-all duration-300 border-orange-500/20 overflow-hidden">
                  {post.image_url && (
                    <img src={post.image_url} alt={post.title} className="w-full h-48 object-cover" loading="lazy" />
                  )}
                  <CardHeader>
                    <div className="text-sm text-orange-300 mb-2">{post.category}</div>
                    <CardTitle className="text-xl text-white">{post.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-200 line-clamp-3">{post.excerpt}</p>
                    <div className="text-sm text-gray-300 mt-4">
                      {new Date(post.published_at).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
            {posts.map((post) => (
              <Link key={post.id} to={`/${post.slug}`}>
                <Card className="bg-gradient-to-br from-orange-500 to-black/90 hover:from-orange-600 hover:to-black transition-all duration-300 border-orange-500/20">
                  <CardHeader>
                    <div className="text-sm text-orange-300 mb-2">{post.category}</div>
                    <CardTitle className="text-xl text-white">{post.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-200 line-clamp-3">{post.excerpt}</p>
                    <div className="text-sm text-gray-300 mt-4">{post.date}</div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Blog;
